import { useCallback, useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useWallet } from "@/context/WalletContext";
import { useCurrency } from "@/context/CurrencyContext";
import UserWallet from "@/components/UserWallet";
import { ArrowDownToLine, ArrowUpFromLine, ShieldCheck, History, ExternalLink, Download, TrendingUp, TrendingDown, Coins, ChevronDown } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtStx = (micro) => {
  const n = (Number(micro) || 0) / 1_000_000;
  return n.toLocaleString("en-US", { maximumFractionDigits: 6 });
};

const fmtTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  return d.toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit",
  });
};

const snippet = (s) => (s ? `${s.slice(0, 6)}…${s.slice(-4)}` : null);

const fmtUsd = (v) => {
  const n = Number(v) || 0;
  if (n === 0) return "—";
  if (n < 0.01) return `$${n.toPrecision(4)}`;
  return `$${n.toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
};

const GREEN = "text-green-400";
const RED = "text-red-400";

// Resolve per-row display props. Perp closes and flip games are colored by
// their realized P&L (green gain / red loss); perp opens are neutral orange
// (capital deployed into a position).
const rowMeta = (t) => {
  switch (t.type) {
    case "deposit":
      return { label: "Deposit", color: GREEN, Icon: ArrowDownToLine, ring: "bg-green-500/10 border-green-500/30" };
    case "withdrawal":
      return { label: "Withdrawal", color: RED, Icon: ArrowUpFromLine, ring: "bg-red-500/10 border-red-500/30" };
    case "admin_adjustment":
      return { label: "Admin Adjustment", color: "text-sky-400", Icon: ShieldCheck, ring: "bg-sky-500/10 border-sky-500/30" };
    case "perp_open":
      return { label: "Perp Opened", color: "text-orange-400", Icon: TrendingUp, ring: "bg-orange-500/10 border-orange-500/30" };
    case "perp_close": {
      const gain = (t.realized_pnl_stxoshi || 0) >= 0;
      return { label: "Perp Closed", color: gain ? GREEN : RED, Icon: gain ? TrendingUp : TrendingDown, ring: gain ? "bg-green-500/10 border-green-500/30" : "bg-red-500/10 border-red-500/30" };
    }
    case "flip_game": {
      const gain = (t.realized_pnl_stxoshi || 0) >= 0;
      return { label: "Flip", color: gain ? GREEN : RED, Icon: Coins, ring: gain ? "bg-green-500/10 border-green-500/30" : "bg-red-500/10 border-red-500/30" };
    }
    default:
      return { label: t.type, color: "text-muted-foreground", Icon: History, ring: "bg-muted/20 border-border/50" };
  }
};

/**
 * UserWalletPanel — mobile-first ("no side-to-side scroll") User Wallet section
 * for the Profile page. Reuses the shared <UserWallet> card for the live coin
 * balance + Deposit/Withdraw buttons, then renders a unified transaction ledger
 * (deposits green, withdrawals red, admin adjustments blue) with per-row
 * timestamp, tx snippet and running balance.
 */
export default function UserWalletPanel() {
  const { isConnected, getAuthHeaders } = useWallet();
  const { showUsd, toggleUsd, stxoshiUsd } = useCurrency();
  const unitLabel = showUsd ? "USD" : "STXOSHI";
  // USD-aware amount formatter (shadows the module-level fmtStx inside this
  // component so the ledger + running balance follow the global toggle).
  const fmtStx = (micro) => {
    const n = (Number(micro) || 0) / 1_000_000;
    if (showUsd) {
      const usd = n * (stxoshiUsd || 0.0000037);
      return `$${usd.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: Math.abs(usd) < 0.01 ? 8 : 2 })}`;
    }
    return n.toLocaleString("en-US", { maximumFractionDigits: 6 });
  };
  const [txns, setTxns] = useState([]);
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [expandedId, setExpandedId] = useState(null);

  const fetchTxns = useCallback(async () => {
    if (!isConnected) return;
    try {
      const r = await fetch(`${API}/api/flip-market/player-wallet/transactions`, {
        headers: getAuthHeaders(),
      });
      const d = await r.json();
      if (r.ok) {
        setTxns(d.transactions || []);
        setBalance(d.balance_stxoshi || 0);
      }
    } catch (e) {
      console.error("[UserWalletPanel] fetch failed:", e);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected]);

  useEffect(() => {
    fetchTxns();
  }, [fetchTxns, refreshKey]);

  const exportCsv = async () => {
    const header = ["Type", "Timestamp", "Amount (STXOSHI)", "Running Balance (STXOSHI)", "Realized P&L (STXOSHI)", "Tx ID", "Details"];
    const rows = txns.map((t) => {
      const signed = (t.signed_stxoshi || 0) / 1_000_000;
      const running = (t.running_balance_stxoshi || 0) / 1_000_000;
      const pnl = t.realized_pnl_stxoshi != null ? (t.realized_pnl_stxoshi / 1_000_000) : "";
      const label = rowMeta(t).label;
      const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
      return [
        esc(label),
        esc(t.timestamp || ""),
        esc(signed),
        esc(running),
        esc(pnl),
        esc(t.tx_id || ""),
        esc(t.reason || ""),
      ].join(",");
    });
    const csv = [header.join(","), ...rows].join("\n");
    const filename = `user-wallet-transactions-${new Date().toISOString().slice(0, 10)}.csv`;

    // On iOS Safari the <a download> attribute + blob URLs are ignored, so the
    // button appears to "do nothing". Prefer the Web Share API with a File,
    // which triggers the native iOS share sheet ("Save to Files", AirDrop, …).
    try {
      const file = new File([csv], filename, { type: "text/csv" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "User Wallet Transactions" });
        return;
      }
    } catch (e) {
      if (e && e.name === "AbortError") return; // user dismissed the share sheet
      // otherwise fall through to the download fallback below
    }

    // Desktop / unsupported-share fallback: classic anchor download.
    try {
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // Last-resort fallback: open the CSV inline so the user can save it.
      const dataUri = "data:text/csv;charset=utf-8," + encodeURIComponent(csv);
      window.open(dataUri, "_blank");
    }
  };

  if (!isConnected) return null;

  return (
    <Card className="glass-card border-border/60 overflow-hidden" data-testid="user-wallet-panel">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2">
          <History className="w-4 h-4 text-orange-400" />
          User Wallet
        </CardTitle>
        <button
          onClick={toggleUsd}
          className="text-xs px-2.5 py-1 rounded-full border border-border text-muted-foreground hover:bg-muted/50 transition-colors font-mono"
          data-testid="wallet-usd-toggle"
          title="Toggle STXOSHI / USD"
        >
          {showUsd ? "USD" : "STXOSHI"}
        </button>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Live balance + Deposit / Withdraw buttons (shared component). */}
        <UserWallet
          onBalanceChange={(b) => setBalance(b)}
          refreshKey={refreshKey}
        />

        {/* Ledger */}
        <div>
          <div className="flex items-center justify-between mb-2 gap-2">
            <span className="text-xs uppercase tracking-wider text-muted-foreground">
              Transaction History
            </span>
            <div className="flex items-center gap-2 flex-shrink-0">
              {txns.length > 0 && (
                <button
                  onClick={exportCsv}
                  className="inline-flex items-center gap-1 text-xs text-orange-400 hover:text-orange-300 border border-orange-500/30 rounded-md px-2 py-1 transition-colors"
                  data-testid="wallet-export-csv-btn"
                >
                  <Download className="w-3 h-3" /> CSV
                </button>
              )}
              <span className="text-xs text-muted-foreground" data-testid="wallet-running-balance">
                <span className="text-green-400 font-mono">{fmtStx(balance)}</span>
              </span>
            </div>
          </div>

          {loading ? (
            <div className="text-sm text-muted-foreground py-6 text-center">Loading…</div>
          ) : txns.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center" data-testid="wallet-txns-empty">
              No deposits or withdrawals yet. Use the buttons above to fund your User Wallet.
            </div>
          ) : (
            <div className="flex flex-col gap-2 max-h-[380px] overflow-y-auto pr-1" data-testid="wallet-txn-list">
              {txns.map((t) => {
                const meta = rowMeta(t);
                const { Icon } = meta;
                const isNeg = (t.signed_stxoshi || 0) < 0;
                const sign = isNeg ? "−" : "+";
                const explorer = t.tx_id
                  ? `https://explorer.hiro.so/txid/${t.tx_id}?chain=mainnet`
                  : null;
                const hasPnl = t.realized_pnl_stxoshi != null;
                const pnlGain = (t.realized_pnl_stxoshi || 0) >= 0;
                const isPerp = t.type === "perp_open" || t.type === "perp_close";
                const isOpen = expandedId === t.id;
                return (
                  <div
                    key={t.id}
                    className="rounded-xl border border-border/50 bg-muted/20 min-w-0"
                    data-testid={`wallet-txn-${t.type}`}
                  >
                    <div
                      className={`flex items-center gap-3 p-3 min-w-0 ${isPerp ? "cursor-pointer" : ""}`}
                      onClick={isPerp ? () => setExpandedId(isOpen ? null : t.id) : undefined}
                      data-testid={isPerp ? `wallet-txn-toggle-${t.id}` : undefined}
                    >
                    {/* Icon */}
                    <div className={`flex-shrink-0 w-9 h-9 rounded-full border flex items-center justify-center ${meta.ring}`}>
                      <Icon className={`w-4 h-4 ${meta.color}`} />
                    </div>

                    {/* Middle: label + meta (truncates, never overflows) */}
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm font-semibold ${meta.color} truncate flex items-center gap-1`}>
                        {meta.label}
                        {t.recovered && (
                          <span className="text-[10px] uppercase text-cyan-400 border border-cyan-500/30 rounded px-1 py-0.5">
                            recovered
                          </span>
                        )}
                        {isPerp && (
                          <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${isOpen ? "rotate-180" : ""}`} />
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {fmtTime(t.timestamp)}
                      </div>
                      {t.reason && (t.type === "admin_adjustment" || isPerp || t.type === "flip_game") ? (
                        <div className={`text-xs truncate ${t.type === "admin_adjustment" ? "text-sky-300/80" : "text-muted-foreground"}`}>{t.reason}</div>
                      ) : explorer ? (
                        <a
                          href={explorer}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-xs font-mono text-muted-foreground hover:text-orange-400 inline-flex items-center gap-1 truncate"
                        >
                          {snippet(t.tx_id)}
                          <ExternalLink className="w-3 h-3 flex-shrink-0" />
                        </a>
                      ) : null}
                    </div>

                    {/* Right: amount (+ P&L badge for perps/flips) + running balance */}
                    <div className="flex-shrink-0 text-right">
                      <div className={`text-sm font-mono font-bold ${meta.color} whitespace-nowrap`}>
                        {sign}{fmtStx(t.amount_stxoshi)}
                      </div>
                      {hasPnl && (
                        <div className={`text-[11px] font-mono whitespace-nowrap ${pnlGain ? GREEN : RED}`}>
                          {pnlGain ? "+" : "−"}{fmtStx(Math.abs(t.realized_pnl_stxoshi))} P&L
                        </div>
                      )}
                      <div className="text-[11px] text-muted-foreground font-mono whitespace-nowrap">
                        bal {fmtStx(t.running_balance_stxoshi)}
                      </div>
                    </div>
                    </div>

                    {/* Expandable per-position trade detail */}
                    {isPerp && isOpen && (
                      <div className="px-3 pb-3 -mt-1" data-testid={`wallet-txn-detail-${t.id}`}>
                        <div className="rounded-lg border border-border/50 bg-background/40 p-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                          <Detail label="Market" value={`${t.market}-PERP ${t.side} ${t.leverage}×`} />
                          <Detail label="Notional" value={fmtStx(t.notional_stxoshi)} />
                          <Detail label="Margin" value={fmtStx(t.margin_stxoshi)} />
                          <Detail label="Entry" value={fmtUsd(t.entry_price_usd)} />
                          {t.type === "perp_close" && <Detail label="Exit" value={fmtUsd(t.close_price_usd)} />}
                          {t.type === "perp_close" && (
                            <Detail label="Gross P&L" value={`${(t.gross_pnl_stxoshi || 0) >= 0 ? "+" : "−"}${fmtStx(Math.abs(t.gross_pnl_stxoshi || 0))}`} valueClass={(t.gross_pnl_stxoshi || 0) >= 0 ? GREEN : RED} />
                          )}
                          {t.type === "perp_close" && (
                            <Detail label="Funding paid" value={`−${fmtStx(t.funding_stxoshi)}`} valueClass="text-orange-300" />
                          )}
                          {t.type === "perp_close" && (
                            <Detail label="Net P&L" value={`${pnlGain ? "+" : "−"}${fmtStx(Math.abs(t.realized_pnl_stxoshi || 0))}`} valueClass={pnlGain ? GREEN : RED} />
                          )}
                          {t.type === "perp_close" && (
                            <Detail label="Returned" value={`+${fmtStx(t.return_to_user_stxoshi)}`} valueClass={GREEN} />
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function Detail({ label, value, valueClass = "text-foreground" }) {
  return (
    <div className="flex items-center justify-between gap-2 min-w-0">
      <span className="text-muted-foreground whitespace-nowrap">{label}</span>
      <span className={`font-mono truncate text-right ${valueClass}`}>{value}</span>
    </div>
  );
}
